import { forkJoin, iif as observableIif, Observable, of } from 'rxjs';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { UserInfo } from '../shared/user-info.model';
import { UserService } from '../shared/user.service';
import { LoaderService } from '../../shared/services/loader.service';
import { DisplayMode } from '../../shared/display-mode';
import { Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { Carrier } from '../shared/carrier.model';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalService } from '../../shared/services/modal.service';
import { ComponentCanDeactivate } from '../../core/guards/pending-changes.guard';
import { UserFormComponent } from '../user-form/user-form.component';
import { NavigationService } from '../../shared/services/navigation.service';
import { finalize, map, tap } from 'rxjs/operators';
import { FeatureProfile } from '../shared/feature-profile.model';
import { Role } from '../shared/role.model';
import { RoleService } from '../shared/role.service';


@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit, ComponentCanDeactivate {

  activeTab: String    = 'profile';
  isActionDropdownOpen = false;

  user          = new UserInfo();
  carrier       = new Carrier(new FeatureProfile());
  roles: Role[] = [];

  onCreate: (u: UserInfo) => Observable<UserInfo>;
  onUpdate: (u: UserInfo) => Observable<UserInfo>;
  onDelete: (u: UserInfo) => Observable<boolean>;
  onToggleBlocking: (u: UserInfo) => Observable<boolean>;

  mode = DisplayMode.Creation;

  DisplayMode = DisplayMode;

  formTitle = 'New User';

  selectableRoles: Observable<Role[]>;
  selectedRoles: Role[];

  @ViewChild('userForm')
  userFormComponent: UserFormComponent;

  constructor(
    private route: ActivatedRoute,
    private navigation: NavigationService,
    private location: Location,
    private authService: AuthenticationService,
    private userService: UserService,
    private roleService: RoleService,
    private modalService: ModalService,
    private loader: LoaderService,
    private toastr: ToastrService,
  ) {
    this.onCreate = (u => {
      return this.userService.createUser(u);
    });

    this.onUpdate = (u => {
      return this.userService.updateUser(u);
    });

    this.onDelete = (u => {
      return this.userService.deleteUser(u.id);
    });

    this.onToggleBlocking = (u => {
      return this.userService.toggleBlockingUser(u);
    });
  }

  ngOnInit() {
    this.route.params.subscribe((params: Params): void => {
      const userId = params.id;

      this.loader.startLoad(false);

      const userObservable = observableIif(
        () => !!userId,
        this.userService.getById(userId).pipe(tap(() => this.switchToUpdateMode())),
        of(new UserInfo())
      );

      const rolesObservable: Observable<Role[]> = observableIif(
        () => !!userId,
        this.userService.getRoles(userId),
        of([])
      );

      forkJoin([userObservable, rolesObservable])
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(([driver, roles]) => {
          this.user    = driver;
          this.roles   = roles;
          this.carrier = driver.carrier;
        }, (error: HttpErrorResponse) => {
          console.log(error);
          this.toastr.error(error.error.message || 'Error while fetching the user', 'Error');
        });
    });
  }

  getForm() {
    return this.userFormComponent.form;
  }

  switchToUpdateMode(): void {
    this.mode      = DisplayMode.Edition;
    this.formTitle = 'User Edition';
  }

  goToUserList() {
    this.navigation.users()
      .list()
      .go();
  }

  changePassword(password: string) {
    this.loader.startLoad();
    this.userService.updatePassword(this.user, password)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.toastr.success('The password has been successfully updated');
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(error.error.message || 'Error while updating the password');
      });
  }

  doRemoveUserFromRole(roleId: string): void {
    this.loader.startLoad();
    this.userService.removeUserFromRole(this.user.id, roleId)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        const idx = this.roles.map(g => g.id).indexOf(roleId);
        this.roles.splice(idx, 1);
      });
  }

  openAddRoleModal(content) {
    const roleIds        = this.roles.map(e => e.id);
    this.selectableRoles = this.roleService.getAll()
      .pipe(
        map(elt => elt.filter(entry => roleIds.indexOf(entry.id) < 0))
      );

    this.modalService.open({
      title: 'Add Role Membership',
      content,
      validateButton: {
        text: 'Add',
        icon: 'fa fa-save',
        disabled: () => !this.selectedRoles || this.selectedRoles.length < 1
      }
    }).subscribe(() => {
      this.selectedRoles.forEach(grp => {
        this.loader.startLoad();

        this.userService.addUserToRole(this.user.id, grp.id)
          .pipe(finalize(() => this.loader.stopLoad()))
          .subscribe(() => {
            this.roles.push(grp);
          });
      });
    });
  }

  openRemoveFromRoleConfirm(content, roleId) {
    this.modalService.open({
      title: 'Remove Member',
      content,
      closeButton: {
        text: 'No, Keep it',
      },
      validateButton: {
        type: 'danger',
        text: 'Yes, remove it',
        icon: 'fa fa-trash',
      }
    }).subscribe(() => this.doRemoveUserFromRole(roleId));
  }

}
